import React, { useEffect } from 'react';
import { useNotificationStore } from '../stores/notificationStore';
import { format, formatDistanceToNow } from 'date-fns';
import { Bell, Calendar, Users, Star, ChevronRight, Mail, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';

export default function Notifications() {
  const { notifications, fetchNotifications, markAsRead } = useNotificationStore();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [fetchNotifications, user]);

  // Group notifications by date
  const groupedNotifications = notifications.reduce((groups, notification) => {
    const date = new Date(notification.created_at).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(notification);
    return groups;
  }, {} as Record<string, typeof notifications>);

  // Get icon based on notification type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_subscriber':
        return <Users className="h-5 w-5 text-green-600" />;
      case 'subscription_cancelled':
        return <Users className="h-5 w-5 text-red-600" />;
      case 'calendar_message':
        return <MessageSquare className="h-5 w-5 text-purple-600" />;
      case 'calendar_invite':
        return <Mail className="h-5 w-5 text-blue-600" />;
      case 'event_feedback':
        return <Star className="h-5 w-5 text-yellow-600" />;
      default:
        return <Bell className="h-5 w-5 text-purple-600" />;
    }
  };

  // Get notification message
  const getNotificationMessage = (notification: any) => {
    switch (notification.type) {
      case 'new_subscriber':
        return (
          <div>
            <p>
              <span className="font-medium">{notification.data.subscriberName}</span> subscribed to{' '}
              <span className="font-medium">{notification.data.calendarName}</span>
            </p>
          </div>
        );
      case 'subscription_cancelled':
        return (
          <div>
            <p>
              <span className="font-medium">{notification.data.subscriberName}</span> cancelled their subscription to{' '}
              <span className="font-medium">{notification.data.calendarName}</span>
            </p>
          </div>
        );
      case 'calendar_message':
        return (
          <div>
            <p>
              New message from <span className="font-medium">{notification.data.calendarName}</span>
            </p>
            <div className="mt-2 p-3 bg-gray-50 rounded-md text-gray-700 whitespace-pre-wrap">
              {notification.data.message}
            </div>
          </div>
        );
      case 'calendar_invite':
        return (
          <div>
            <p>
              You've been invited to join{' '}
              <span className="font-medium">{notification.data.calendarName}</span>
            </p>
            <p className="mt-1 text-sm text-gray-600">
              Click to view and subscribe to this calendar
            </p>
          </div>
        );
      case 'event_feedback':
        return (
          <div>
            <p>
              New feedback received for event in{' '}
              <span className="font-medium">{notification.data.calendarName}</span>
            </p>
          </div>
        );
      default:
        return 'New notification';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <SEO 
        title="Notifications" 
        description="View your notifications and updates"
        noindex={true}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-3 mb-8">
          <Bell className="h-8 w-8 text-purple-600" />
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
        </div>

        {Object.entries(groupedNotifications).length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No notifications yet</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedNotifications)
              .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
              .map(([date, dayNotifications]) => (
                <div key={date}>
                  <h2 className="text-sm font-medium text-gray-500 mb-4">
                    {format(new Date(date), 'MMMM d, yyyy')}
                  </h2>
                  <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
                    {dayNotifications.map(notification => (
                      <Link
                        key={notification.id}
                        to={`/calendar/${notification.data.calendarId}`}
                        className={`block p-4 hover:bg-gray-50 transition-colors ${
                          !notification.read ? 'bg-purple-50' : ''
                        }`}
                        onClick={() => !notification.read && markAsRead(notification.id)}
                      >
                        <div className="flex items-start space-x-4">
                          <div className="flex-shrink-0 pt-1">
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            {getNotificationMessage(notification)}
                            <p className="mt-2 text-xs text-gray-500">
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}